import { useMemo } from 'react';
import { useFeatureFlags, useGtmService } from '@funtech-int/yango-shared';

import { useInstanceLayoutEffect } from '@/scenarios/legacy-player/hooks/useInstanceLayoutEffect';

import { featureFlagsScheme } from '../../../../lib/featureFlags';
import { usePlayerContentFinished } from '../../analytics/hooks/use-player-content-finished';
import { usePlayerContentStarted } from '../../analytics/hooks/use-player-content-started';

interface UsePlayerAnalyticsParams {
    contentId: string | undefined;
    psid: string;
    manifestUrl: string | undefined;
    viewStartedGtmEvent?: string;
}

export const usePlayerAnalytics = ({
    contentId,
    psid,
    manifestUrl,
    viewStartedGtmEvent,
}: UsePlayerAnalyticsParams) => {
    const { flags } = useFeatureFlags(featureFlagsScheme);
    const gtmService = useGtmService();

    const isDynamicPrerollEnabled = flags.get('isDynamicPrerollEnabled');

    const eventMeta = useMemo(
        () => ({
            psid: psid ?? '',
            contentId: contentId ?? '',
            manifestUrl: manifestUrl ?? '',
        }),
        [psid, contentId, manifestUrl]
    );

    const trackedPlayerContentStarted = usePlayerContentStarted(eventMeta);
    const trackedPlayerContentFinished = usePlayerContentFinished(eventMeta);

    useInstanceLayoutEffect(
        (instance) => {
            const unsubscribeFromOnContentImpression = instance.onContentImpression(() => {
                trackedPlayerContentStarted({ vsid: instance.vsid ?? '', isDynamicPrerollEnabled });

                if (viewStartedGtmEvent) {
                    gtmService?.send({ event: viewStartedGtmEvent });
                }
            });
            const unsubscribeFromOnFinish = instance.onFinish(() =>
                trackedPlayerContentFinished({ vsid: instance.vsid ?? '', isDynamicPrerollEnabled })
            );

            return () => {
                unsubscribeFromOnContentImpression();
                unsubscribeFromOnFinish();
            };
        },
        [
            trackedPlayerContentStarted,
            trackedPlayerContentFinished,
            isDynamicPrerollEnabled,
            gtmService,
            viewStartedGtmEvent,
        ]
    );
};
